import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import {
  MatDialog,
  MatDialogRef,
  MatSnackBar,
  MAT_DIALOG_DATA,
} from "@angular/material";
import { DomSanitizer } from "@angular/platform-browser";
import { ConfirmationDialogComponent } from "@app/main/content/confirmation-dialog/confirmation-dialog.component";
import { LoopBackConfig, Organization } from "@app/shared/sdk";
import { environment } from "@env/environment";
import { FuseSplashScreenService } from "@fuse/services/splash-screen.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { GeneralSettingsService } from "../../general-settings.service";

@Component({
  selector: "app-organizations-dialog",
  templateUrl: "./organizations-dialog.component.html",
  styleUrls: ["./organizations-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class OrganizationsDialogComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  dialogTitle: string;
  organizationForm: FormGroup;
  organizationEditForm: FormGroup;
  action: string;
  organizations: Organization[] = [];
  s3BucketUrl: string;
  orgLogo: any;
  orgLogoUrl: any;
  orgThankYouImg: any;
  orgThankYouImgUrl: any;

  organization: Organization = new Organization();

  organizationClicked = false;

  url: string =
    LoopBackConfig.getPath() +
    "/" +
    LoopBackConfig.getApiVersion() +
    "/containers/" +
    encodeURIComponent(
      environment.mainContainer + "/" + environment.oraganizationUploadPath
    ) +
    "/upload";

  fileNameDialogRef: any;

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    public dialogRef: MatDialogRef<OrganizationsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private domSanitizer: DomSanitizer,
    private fuseSplashScreenService: FuseSplashScreenService,
    private generalSettingsService: GeneralSettingsService,
    private cdRef: ChangeDetectorRef
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();

    this.s3BucketUrl = environment.s3BucketUrl;

    this.action = data.action;
    this.dialogTitle = "Organization Info";
    this.organizationForm = this.createOrganizationForm();
    this.organizationEditForm = this.editOrganizationForm();
  }

  ngOnInit() {
    this.orgLogoUrl = "";
    this.orgThankYouImgUrl = "";
  }

  ngAfterViewInit() {
    this.initializeOrganizations();
    // Subscribe to update organizations on changes
    this.generalSettingsService.onOrganizationsChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((organizations: any[]) => {
        this.organizations = organizations;
      });

    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  initializeOrganizations() {
    this.generalSettingsService
      .getOrganizations(this.data.donationCategoryId)
      .then(
        (organizations: any[]) => {
          this.organizations = organizations;
        },
        (error) => {
          this.snackBar.open("Internal server error", "ERROR", {
            verticalPosition: "top",
            duration: 2000,
          });
        }
      );
  }

  createOrganizationForm() {
    return this.formBuilder.group({
      name: ["", Validators.required],
      logo: ["", Validators.required],
      info: ["", Validators.required],
      thankYouImg: [""],
    });
  }

  editOrganizationForm() {
    return this.formBuilder.group({
      name: [this.organization.name ? this.organization.name : ""],
      info: [this.organization.info ? this.organization.info : ""],
    });
  }

  deleteOrganization() {
    this.fileNameDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: "Are you sure you want to Delete?",
      },
    });
    this.fileNameDialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(async (result) => {
        if (!result) return;
        this.fuseSplashScreenService.show();
        try {
          const response = await this.generalSettingsService
            .deleteOrgranization(this.organization.id)
            .toPromise();
          this.fuseSplashScreenService.hide();
          if (!response) return;

          let index = this.organizations.findIndex(
            (el) => el.id === this.organization.id
          );
          this.organizations.splice(index, 1);
          this.organizationClicked = false;

          this.snackBar.open("Oraganizaton delete successfully", "SUCCESS", {
            verticalPosition: "top",
            duration: 2000,
          });
        } catch (error) {
          this.fuseSplashScreenService.hide();
          this.snackBar.open(
            "Problem Deleting Organization, Please Try Again!",
            "ERROR",
            {
              verticalPosition: "top",
              duration: 2000,
            }
          );
          return error;
        }
      });
  }

  addOrganization = async () => {
    const organization = this.organizationForm.getRawValue();
    this.fuseSplashScreenService.show();
    const logoFile = organization.logo._files[0];

    const formData = new FormData();
    formData.append("logo", logoFile);

    if (organization && organization.thankYouImg !== '') {
      const thankYouImgFile = organization.thankYouImg._files[0];
      formData.append("thankYouImg", thankYouImgFile);
    }

    try {
      const uploadCategoryImageResponse: any = await this.generalSettingsService
        .uploadCategoryImage(this.url, formData)
        .toPromise();

      if (!uploadCategoryImageResponse) return;

      const response = await this.generalSettingsService
        .addOrganization({
          name: organization.name,
          logo: uploadCategoryImageResponse.result.files["logo"][0][
            "providerResponse"
          ].name,
          thankYouImg: uploadCategoryImageResponse.result.files["thankYouImg"] ?
            uploadCategoryImageResponse.result.files["thankYouImg"][0][
            "providerResponse"
            ].name : null,
          info: organization.info,
          donationCategoryId: this.data.donationCategoryId,
        })
        .toPromise();

      this.fuseSplashScreenService.hide();
      if (!response) return;

      if (this.generalSettingsService.organizations.length !== 0) {
        this.generalSettingsService.organizations.unshift(response);
      } else {
        this.generalSettingsService.organizations.push(response);
      }
      this.generalSettingsService.onOrganizationsChanged.next(
        this.generalSettingsService.organizations
      );

      this.organizationForm.reset();

      this.snackBar.open("Oraganizaton added successfully", "OK", {
        verticalPosition: "top",
        duration: 2000,
      });
    } catch (error) {
      this.fuseSplashScreenService.hide();
      this.snackBar.open("Problem uploading image", "ERROR", {
        verticalPosition: "top",
        duration: 2000,
      });
      return error;
    }
  };

  setOrgLogo($event) {
    this.orgLogo = $event.target.files;
    const blob = new Blob([$event.target.files[0]], { type: "image/png" });
    this.orgLogoUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(
      URL.createObjectURL(blob)
    );
  }

  setOrgThankYouImg($event) {
    this.orgThankYouImg = $event.target.files;
    const blob = new Blob([$event.target.files[0]], { type: "image/png" });
    this.orgThankYouImgUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(
      URL.createObjectURL(blob)
    );
  }



  editOrganization = async () => {
    const data = this.organizationEditForm.getRawValue();
    let fileResponse = { logo: null, thankYouImg: null };

    if (this.orgLogo !== undefined) {
      const logoFile = this.orgLogo[0];

      const formData = new FormData();
      formData.append("logo", logoFile);

      try {
        this.fuseSplashScreenService.show();
        const uploadingFileToBucketResponse: any =
          await this.generalSettingsService
            .uploadCategoryImage(this.url, formData)
            .toPromise();
        if (!uploadingFileToBucketResponse) {
          this.fuseSplashScreenService.hide();
          return;
        }
        fileResponse.logo = uploadingFileToBucketResponse;
      } catch (error) {
        this.fuseSplashScreenService.hide();
        this.snackBar.open("Problem updating organization logo", "ERROR", {
          verticalPosition: "top",
          duration: 2000,
        });
        return error;
      }
    }

    if (this.orgThankYouImg !== undefined) {
      const formData = new FormData();
      const thankYouImgFile = this.orgThankYouImg[0];
      formData.append("thankYouImg", thankYouImgFile);
      try {
        this.fuseSplashScreenService.show();
        const uploadingFileToBucketResponse: any =
          await this.generalSettingsService
            .uploadCategoryImage(this.url, formData)
            .toPromise();
        if (!uploadingFileToBucketResponse) {
          this.fuseSplashScreenService.hide();
          return;
        }
        fileResponse.thankYouImg = uploadingFileToBucketResponse;
      } catch (error) {
        this.fuseSplashScreenService.hide();
        this.snackBar.open("Problem updating organization thank you image", "ERROR", {
          verticalPosition: "top",
          duration: 2000,
        });
        return error;
      }
    }

    try {
      this.fuseSplashScreenService.show();
      let obj: any = {
        name: data.name,
        info: data.info,
      };
      if (fileResponse.logo) {
        obj.logo = fileResponse.logo;
      }
      if (fileResponse.thankYouImg) {
        obj.thankYouImg = fileResponse.thankYouImg;
      }

      console.log('obj....', obj);

      const response = await this.generalSettingsService
        .editOrganization(this.organization.id, obj)
        .toPromise();

      if (!response) {
        this.fuseSplashScreenService.hide();
        return;
      }

      this.organizations.forEach((el, index) => {
        if (el.id === this.organization.id) {
          this.organizations[index] = response;
        }
      });
      this.generalSettingsService.onOrganizationsChanged.next(
        this.organizations
      );

      this.snackBar.open("Successfully updated organization", "OK", {
        verticalPosition: "top",
        duration: 2000,
      });
      this.fuseSplashScreenService.hide();
    } catch (error) {
      console.log('error....', error);
      this.fuseSplashScreenService.hide();
      this.snackBar.open("Problem updating organization", "ERROR", {
        verticalPosition: "top",
        duration: 2000,
      });
      return error;
    }
  };

  openOraganizationInfo(flag: boolean, organization?) {
    this.organizationClicked = flag;
    if (organization) {
      this.orgLogoUrl = "";
      this.orgThankYouImgUrl = "";
      this.organization = organization;
      this.organizationEditForm.patchValue({
        name: organization.name,
        info: organization.info,
      });
    }
  }
}
